import {FC, ReactNode, useCallback, useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {useNavigate, useParams, Link} from 'react-router-dom'
import {toast} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {KTCard, KTCardBody, KTSVG} from '../../../_dashboard/helpers'
import {AzPageHeader} from '../../components/AzPageHeader'
import {DataLoading} from '../../../_dashboard/partials/components/loading/DataLoading'
import {cancelMailOrder, getMailOrderDetails, sendInstallmentLink} from '../../services/mailOrders'

const STATUS_BADGE_CLASS: Record<string, string> = {
  pending: 'badge-light-warning',
  partially_paid: 'badge-light-info',
  paid: 'badge-light-success',
  expired: 'badge-light-danger',
  cancelled: 'badge-light-dark',
  processing: 'badge-light-primary',
}

const STATUS_LABEL_KEY: Record<string, string> = {
  pending: 'MAIL_ORDER.STATUS.PENDING',
  partially_paid: 'MAIL_ORDER.STATUS.PARTIALLY_PAID',
  paid: 'MAIL_ORDER.STATUS.PAID',
  expired: 'MAIL_ORDER.STATUS.EXPIRED',
  cancelled: 'MAIL_ORDER.STATUS.CANCELLED',
  processing: 'MAIL_ORDER.PAYMENT_ATTEMPT.STATUS.PROCESSING',
}

const ATTEMPT_STATUS_LABEL_KEY: Record<string, string> = {
  processing: 'MAIL_ORDER.PAYMENT_ATTEMPT.STATUS.PROCESSING',
  success: 'MAIL_ORDER.PAYMENT_ATTEMPT.STATUS.SUCCESS',
  failed: 'MAIL_ORDER.PAYMENT_ATTEMPT.STATUS.FAILED',
}

const LANGUAGE_LABEL_KEY: Record<string, string> = {
  fr: 'MAIL_ORDER.FIELDS.LANGUAGE.FR',
  en: 'MAIL_ORDER.FIELDS.LANGUAGE.EN',
  de: 'MAIL_ORDER.FIELDS.LANGUAGE.DE',
}

const formatAmount = (amount?: number, currency?: string) => {
  if (typeof amount !== 'number') return '-'
  try {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency || 'TND',
      maximumFractionDigits: 3,
    }).format(amount)
  } catch {
    return `${amount} ${currency || ''}`
  }
}

const formatAmountValue = (amount?: number) => {
  if (typeof amount !== 'number') return '-'
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(amount)
}

const formatDate = (value?: string) => {
  if (!value) return '-'
  const date = new Date(value)
  return isNaN(date.getTime()) ? '-' : date.toLocaleDateString('fr-FR')
}

const formatDateTime = (value?: string) => {
  if (!value) return '-'
  const date = new Date(value)
  return isNaN(date.getTime()) ? '-' : date.toLocaleString('fr-FR')
}

type AuditUser = {
  _id?: string
  firstName?: string
  lastName?: string
  email?: string
}

/** Single account label (avoids "Name Name" when first/last are identical). */
const getAccountName = (user?: AuditUser) => {
  if (!user) return '-'
  const first = user.firstName?.trim() || ''
  const last = user.lastName?.trim() || ''
  if (first && last && first.toLowerCase() === last.toLowerCase()) return first
  const full = [first, last].filter(Boolean).join(' ')
  return full || user.email || '-'
}

const AccountLink: FC<{user?: AuditUser}> = ({user}) => {
  const label = getAccountName(user)
  const userId = user?._id
  if (!userId || label === '-') return <>{label}</>
  return (
    <Link
      to={`/apps/user-management/users?view=${userId}`}
      className='text-primary text-hover-primary fw-semibold'
    >
      {label}
    </Link>
  )
}

type TabId = 'overview' | 'installments' | 'history'

const InfoRow: FC<{label: string; icon?: string; children: ReactNode; last?: boolean}> = ({
  label,
  icon,
  children,
  last,
}) => (
  <div
    className={`bo-info-row d-flex justify-content-between align-items-start py-3 ${
      last ? '' : 'border-bottom border-gray-200'
    }`}
  >
    <span className='text-muted fw-semibold me-4 d-flex align-items-center'>
      {icon ? <i className={`bi ${icon} me-2 text-primary`} /> : null}
      {label}
    </span>
    <span className='text-gray-900 fw-semibold text-end'>{children || '-'}</span>
  </div>
)

const SectionCard: FC<{
  title: string
  icon: string
  children: ReactNode
  className?: string
}> = ({title, icon, children, className}) => (
  <div className={`card shadow-sm border border-gray-200 h-100 ${className || ''}`}>
    <div className='card-body p-6'>
      <div className='d-flex align-items-center gap-3 mb-5'>
        <span className='symbol symbol-40px symbol-circle bg-light-primary'>
          <span className='symbol-label'>
            <i className={`bi ${icon} fs-3 text-primary`} />
          </span>
        </span>
        <h3 className='fs-4 fw-bold text-gray-900 mb-0'>{title}</h3>
      </div>
      {children}
    </div>
  </div>
)

const AmountHighlight: FC<{
  label: string
  amount?: number
  currency?: string
}> = ({label, amount, currency}) => (
  <div className='rounded-3 border border-gray-200 bg-light p-4'>
    <div className='text-muted fw-semibold fs-7 mb-2'>{label}</div>
    <div className='d-flex align-items-end gap-2 flex-wrap'>
      <span className='fs-2 fw-bolder text-gray-900 lh-1'>{formatAmountValue(amount)}</span>
      <span className='fs-7 fw-bold text-gray-600 mb-1'>{currency || 'TND'}</span>
    </div>
  </div>
)

const MailOrderDetailsPage: FC = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const {id} = useParams<{id: string}>()
  const [loading, setLoading] = useState(true)
  const [details, setDetails] = useState<any>(null)
  const [activeTab, setActiveTab] = useState<TabId>('overview')
  const [sendingId, setSendingId] = useState<string | null>(null)

  const loadDetails = useCallback(() => {
    if (!id) return
    setLoading(true)
    getMailOrderDetails(id)
      .then((data: any) => setDetails(data))
      .catch(() => {
        toast.error(intl.formatMessage({id: 'COMMON.ERROR.GENERIC'}), {position: 'top-right'})
      })
      .finally(() => setLoading(false))
  }, [id, intl])

  useEffect(() => {
    loadDetails()
  }, [loadDetails])

  const mailOrder = details?.mailOrder
  const installments = details?.installments || []
  const paymentAttempts = details?.paymentAttempts || []
  const isCancelled = mailOrder?.status === 'cancelled'
  const isPaid = mailOrder?.status === 'paid'
  const paidAmount = details?.paidAmount ?? 0
  const remainingAmount = details?.remainingAmount ?? mailOrder?.totalPrice ?? 0
  const progressPercent =
    mailOrder?.totalPrice > 0 ? Math.min(100, Math.round((paidAmount / mailOrder.totalPrice) * 100)) : 0

  const handleCancel = async () => {
    if (!id) return
    if (!window.confirm(intl.formatMessage({id: 'MAIL_ORDER.CANCEL_CONFIRM'}))) return
    try {
      const response = await cancelMailOrder(id)
      if (response?.payload?.code === 2021) {
        toast.success(intl.formatMessage({id: 'MAIL_ORDER.CANCEL_SUCCESS'}), {
          position: 'top-right',
          autoClose: 4000,
        })
        loadDetails()
      } else {
        toast.error(intl.formatMessage({id: 'COMMON.ERROR.GENERIC'}), {position: 'top-right'})
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message || intl.formatMessage({id: 'COMMON.ERROR.GENERIC'})
      toast.error(Array.isArray(message) ? message.join(', ') : message, {position: 'top-right'})
    }
  }

  const handleCopyLink = (link: string) => {
    if (!link) {
      toast.error(intl.formatMessage({id: 'COMMON.ERROR.GENERIC'}), {position: 'top-right'})
      return
    }
    navigator.clipboard
      .writeText(link)
      .then(() =>
        toast.success(intl.formatMessage({id: 'MAIL_ORDER.LINK_COPIED'}), {
          position: 'top-right',
          autoClose: 3000,
        })
      )
      .catch(() =>
        toast.error(intl.formatMessage({id: 'COMMON.ERROR.GENERIC'}), {position: 'top-right'})
      )
  }

  const handleSendLink = async (installmentId: string) => {
    if (!id) return
    setSendingId(installmentId)
    try {
      const response = await sendInstallmentLink(id, installmentId)
      if (response?.payload?.code === 2205) {
        toast.success(intl.formatMessage({id: 'MAIL_ORDER.LINK_SENT'}), {
          position: 'top-right',
          autoClose: 4000,
        })
        loadDetails()
      } else {
        toast.error(intl.formatMessage({id: 'COMMON.ERROR.GENERIC'}), {position: 'top-right'})
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message || intl.formatMessage({id: 'COMMON.ERROR.GENERIC'})
      toast.error(Array.isArray(message) ? message.join(', ') : message, {position: 'top-right'})
    } finally {
      setSendingId(null)
    }
  }

  const renderStatusBadge = (status: string) => (
    <span className={`badge ${STATUS_BADGE_CLASS[status] || 'badge-light-secondary'}`}>
      {intl.formatMessage({id: STATUS_LABEL_KEY[status] || 'MAIL_ORDER.STATUS.PENDING'})}
    </span>
  )

  const tabs: {id: TabId; label: string}[] = [
    {id: 'overview', label: intl.formatMessage({id: 'MAIL_ORDER.OVERVIEW.TITLE'})},
    {id: 'installments', label: intl.formatMessage({id: 'MAIL_ORDER.INSTALLMENTS.TITLE'})},
    {id: 'history', label: intl.formatMessage({id: 'MAIL_ORDER.HISTORY.TITLE'})},
  ]

  const renderOverview = () => (
    <div className='row g-5'>
      <div className='col-lg-6'>
        <SectionCard
          title={intl.formatMessage({id: 'MAIL_ORDER.SECTION.TRIP'})}
          icon='bi-map'
        >
          <div className='bg-light rounded-3 p-4 mb-4'>
            <div className='text-muted fw-semibold fs-7 mb-1'>
              {intl.formatMessage({id: 'MAIL_ORDER.FIELDS.NAME'})}
            </div>
            <div className='fs-3 fw-bolder text-gray-900'>{mailOrder.circuitName || '-'}</div>
            {mailOrder.bookingReference ? (
              <div className='mt-2'>
                <span className='badge badge-light-primary fw-bold'>
                  {intl.formatMessage({id: 'MAIL_ORDER.FIELDS.BOOKING_REFERENCE'})}:{' '}
                  {mailOrder.bookingReference}
                </span>
              </div>
            ) : null}
          </div>

          <div className='row g-3 mb-2'>
            <div className='col-sm-4'>
              <div className='border border-gray-200 rounded-3 p-3 h-100'>
                <div className='text-muted fs-8 fw-semibold mb-1'>
                  <i className='bi bi-calendar-event me-1 text-primary' />
                  {intl.formatMessage({id: 'MAIL_ORDER.FIELDS.START_DATE'})}
                </div>
                <div className='fw-bold text-gray-900'>
                  {formatDate(mailOrder.circuitStartDate)}
                </div>
              </div>
            </div>
            <div className='col-sm-4'>
              <div className='border border-gray-200 rounded-3 p-3 h-100'>
                <div className='text-muted fs-8 fw-semibold mb-1'>
                  <i className='bi bi-calendar-check me-1 text-primary' />
                  {intl.formatMessage({id: 'MAIL_ORDER.FIELDS.END_DATE'})}
                </div>
                <div className='fw-bold text-gray-900'>{formatDate(mailOrder.circuitEndDate)}</div>
              </div>
            </div>
            <div className='col-sm-4'>
              <div className='border border-gray-200 rounded-3 p-3 h-100'>
                <div className='text-muted fs-8 fw-semibold mb-1'>
                  <i className='bi bi-people me-1 text-primary' />
                  {intl.formatMessage({id: 'MAIL_ORDER.FIELDS.PARTICIPANTS'})}
                </div>
                <div className='fw-bold text-gray-900'>{mailOrder.participantsCount ?? '-'}</div>
              </div>
            </div>
          </div>

          <InfoRow
            label={intl.formatMessage({id: 'MAIL_ORDER.FIELDS.DESCRIPTION'})}
            icon='bi-text-left'
          >
            {mailOrder.description || '-'}
          </InfoRow>
          <InfoRow
            label={intl.formatMessage({id: 'MAIL_ORDER.FIELDS.SUMMARY'})}
            icon='bi-card-text'
            last
          >
            {mailOrder.circuitSummary || '-'}
          </InfoRow>
        </SectionCard>
      </div>

      <div className='col-lg-6'>
        <div className='d-flex flex-column gap-5 h-100'>
          <SectionCard
            title={intl.formatMessage({id: 'MAIL_ORDER.SECTION.CLIENT'})}
            icon='bi-person'
          >
            <InfoRow
              label={intl.formatMessage({id: 'MAIL_ORDER.FIELDS.CLIENT_NAME'})}
              icon='bi-person-badge'
            >
              {mailOrder.clientName}
            </InfoRow>
            <InfoRow label={intl.formatMessage({id: 'MAIL_ORDER.FIELDS.EMAIL'})} icon='bi-envelope'>
              {mailOrder.clientEmail ? (
                <a href={`mailto:${mailOrder.clientEmail}`}>{mailOrder.clientEmail}</a>
              ) : (
                '-'
              )}
            </InfoRow>
            <InfoRow
              label={intl.formatMessage({id: 'MAIL_ORDER.FIELDS.PHONE'})}
              icon='bi-telephone'
            >
              {mailOrder.clientPhone || '-'}
            </InfoRow>
            <InfoRow
              label={intl.formatMessage({id: 'MAIL_ORDER.FIELDS.LANGUAGE'})}
              icon='bi-translate'
            >
              {intl.formatMessage({
                id: LANGUAGE_LABEL_KEY[mailOrder.clientLanguage] || 'MAIL_ORDER.FIELDS.LANGUAGE.FR',
              })}
            </InfoRow>
            <InfoRow
              label={intl.formatMessage({id: 'MAIL_ORDER.FIELDS.COUNTRY'})}
              icon='bi-flag'
            >
              {mailOrder.clientCountry || '-'}
            </InfoRow>
            <InfoRow
              label={intl.formatMessage({id: 'MAIL_ORDER.FIELDS.WITH_VOUCHER'})}
              icon='bi-file-earmark-pdf'
              last
            >
              {intl.formatMessage({
                id: mailOrder.withVoucher ? 'COMMON.YES' : 'COMMON.NO',
              })}
            </InfoRow>
          </SectionCard>

          <SectionCard
            title={intl.formatMessage({id: 'MAIL_ORDER.SECTION.PAYMENT'})}
            icon='bi-wallet2'
          >
            <div className='row g-3 mb-4'>
              <div className='col-md-4'>
                <AmountHighlight
                  label={intl.formatMessage({id: 'MAIL_ORDER.FIELDS.TOTAL_PRICE'})}
                  amount={mailOrder.totalPrice}
                  currency={mailOrder.currency}
                />
              </div>
              <div className='col-md-4'>
                <AmountHighlight
                  label={intl.formatMessage({id: 'MAIL_ORDER.FIELDS.PAID_AMOUNT'})}
                  amount={paidAmount}
                  currency={mailOrder.currency}
                />
              </div>
              <div className='col-md-4'>
                <AmountHighlight
                  label={intl.formatMessage({id: 'MAIL_ORDER.FIELDS.REMAINING_AMOUNT'})}
                  amount={remainingAmount}
                  currency={mailOrder.currency}
                />
              </div>
            </div>

            <div className='mb-4'>
              <div className='d-flex justify-content-between align-items-center mb-2'>
                <span className='text-muted fw-semibold fs-7'>
                  {intl.formatMessage({id: 'MAIL_ORDER.PROGRESS'}, {
                    paid: formatAmount(paidAmount, mailOrder.currency),
                    total: formatAmount(mailOrder.totalPrice, mailOrder.currency),
                  })}
                </span>
                <span className='fw-bold text-gray-800'>{progressPercent}%</span>
              </div>
              <div className='progress h-10px'>
                <div
                  className={`progress-bar ${isPaid ? 'bg-success' : 'bg-primary'}`}
                  role='progressbar'
                  style={{width: `${progressPercent}%`}}
                />
              </div>
            </div>

            <InfoRow
              label={intl.formatMessage({id: 'MAIL_ORDER.CREATED_BY'})}
              icon='bi-person-plus'
            >
              <AccountLink user={mailOrder.createdBy} /> — {formatDateTime(mailOrder.createdAt)}
            </InfoRow>
            <InfoRow
              label={intl.formatMessage({id: 'MAIL_ORDER.UPDATED_BY'})}
              icon='bi-pencil-square'
              last
            >
              <AccountLink user={mailOrder.updatedBy} /> — {formatDateTime(mailOrder.updatedAt)}
            </InfoRow>
          </SectionCard>
        </div>
      </div>
    </div>
  )

  const renderInstallments = () => {
    if (!installments.length) {
      return (
        <div className='text-center text-muted py-10'>
          {intl.formatMessage({id: 'COMMON.EMPTY_DATA'})}
        </div>
      )
    }

    return (
      <div>
        <div className='d-flex align-items-center justify-content-between flex-wrap gap-3 mb-6'>
          <div>
            <h3 className='fs-4 fw-bold text-gray-900 mb-1'>
              {intl.formatMessage({id: 'MAIL_ORDER.INSTALLMENTS.TITLE'})}
            </h3>
            <div className='text-muted fs-7'>
              {intl.formatMessage(
                {id: 'MAIL_ORDER.INSTALLMENTS_COUNT'},
                {count: installments.length}
              )}
            </div>
          </div>
        </div>

        <div className='row g-5'>
          {installments.map((inst: any, index: number) => {
            const paidLabel =
              inst.status === 'paid'
                ? `${formatAmount(inst.paidAmount || inst.amount, mailOrder.currency)} — ${formatDateTime(inst.paidAt)}`
                : '-'

            return (
              <div className='col-md-6' key={inst._id}>
                <div className='card shadow-sm h-100 border border-gray-200'>
                  <div className='card-body p-6 d-flex flex-column'>
                    <div className='d-flex justify-content-between align-items-center mb-5'>
                      <div className='d-flex align-items-center gap-3'>
                        <span className='symbol symbol-40px symbol-circle bg-light-primary'>
                          <span className='symbol-label'>
                            <i className='bi bi-cash-stack fs-3 text-primary' />
                          </span>
                        </span>
                        <span className='fw-bold text-gray-800 fs-6'>
                          {intl.formatMessage({id: 'MAIL_ORDER.FIELDS.AMOUNT'})} #{index + 1}
                        </span>
                      </div>
                      {renderStatusBadge(inst.status)}
                    </div>

                    <div className='d-flex align-items-baseline gap-2 mb-6'>
                      <div className='fs-2x fw-bolder text-gray-900 lh-1'>
                        {formatAmountValue(inst.amount)}
                      </div>
                      <span className='fs-6 fw-semibold text-gray-600'>
                        {mailOrder.currency || 'TND'}
                      </span>
                    </div>

                    <div className='border border-gray-200 rounded-3 p-4 mb-5'>
                      <div className='d-flex justify-content-between align-items-center py-2'>
                        <span className='text-muted fw-semibold fs-7'>
                          <i className='bi bi-calendar3 me-2 text-gray-500' />
                          {intl.formatMessage({id: 'MAIL_ORDER.FIELDS.EXPIRES_AT'})}
                        </span>
                        <span className='fw-bold text-gray-800'>{formatDate(inst.expiresAt)}</span>
                      </div>
                      <div className='separator separator-dashed my-2' />
                      <div className='d-flex justify-content-between align-items-center py-2'>
                        <span className='text-muted fw-semibold fs-7'>
                          <i className='bi bi-wallet2 me-2 text-gray-500' />
                          {intl.formatMessage({id: 'MAIL_ORDER.FIELDS.PAID_AMOUNT'})}
                        </span>
                        <span className='fw-bold text-gray-800 text-end'>{paidLabel}</span>
                      </div>
                      <div className='separator separator-dashed my-2' />
                      <div className='d-flex justify-content-between align-items-center py-2'>
                        <span className='text-muted fw-semibold fs-7'>
                          <i className='bi bi-envelope me-2 text-gray-500' />
                          {intl.formatMessage({id: 'MAIL_ORDER.FIELDS.LINK_SENT_AT'})}
                        </span>
                        <span className='fw-bold text-gray-800'>
                          {inst.linkSentAt ? formatDateTime(inst.linkSentAt) : '-'}
                        </span>
                      </div>
                    </div>

                    <div className='d-flex flex-wrap gap-2 mt-auto pt-1'>
                      <button
                        type='button'
                        className='btn btn-light-primary btn-sm fw-semibold'
                        onClick={() => handleCopyLink(inst.paymentLink)}
                      >
                        <i className='bi bi-clipboard me-1' />
                        {intl.formatMessage({id: 'MAIL_ORDER.COPY_LINK'})}
                      </button>
                      {inst.paymentLink && (
                        <a
                          href={inst.paymentLink}
                          target='_blank'
                          rel='noreferrer'
                          className='btn btn-light btn-sm fw-semibold'
                        >
                          <i className='bi bi-box-arrow-up-right me-1' />
                          {intl.formatMessage({id: 'MAIL_ORDER.OPEN_LINK'})}
                        </a>
                      )}
                      {inst.status !== 'paid' && !isCancelled && (
                        <button
                          type='button'
                          className='btn btn-light-success btn-sm fw-semibold'
                          disabled={sendingId === inst._id}
                          onClick={() => handleSendLink(inst._id)}
                        >
                          <i className='bi bi-send me-1' />
                          {sendingId === inst._id
                            ? intl.formatMessage({id: 'COMMON.PLEASE_WAIT'})
                            : intl.formatMessage({id: 'MAIL_ORDER.SEND_LINK'})}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const renderHistory = () => (
    <div className='table-responsive'>
      <table className='table align-middle table-row-bordered fs-6 gy-5'>
        <thead>
          <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
            <th>{intl.formatMessage({id: 'MAIL_ORDER.FIELDS.ORDER_NUMBER'})}</th>
            <th>{intl.formatMessage({id: 'MAIL_ORDER.FIELDS.STATUS'})}</th>
            <th>{intl.formatMessage({id: 'MAIL_ORDER.FIELDS.AMOUNT'})} (TND)</th>
            <th>{intl.formatMessage({id: 'MAIL_ORDER.FIELDS.TRANSACTION'})}</th>
            <th>{intl.formatMessage({id: 'COLUMNS.CREATED_AT'})}</th>
            <th>{intl.formatMessage({id: 'MAIL_ORDER.FIELDS.FAILURE_REASON'})}</th>
          </tr>
        </thead>
        <tbody className='text-gray-600 fw-bold'>
          {paymentAttempts.length > 0 ? (
            paymentAttempts.map((attempt: any) => (
              <tr key={attempt._id}>
                <td>{attempt.orderNumber}</td>
                <td>
                  <span
                    className={`badge ${
                      attempt.status === 'success'
                        ? 'badge-light-success'
                        : attempt.status === 'failed'
                        ? 'badge-light-danger'
                        : 'badge-light-primary'
                    }`}
                  >
                    {intl.formatMessage({
                      id:
                        ATTEMPT_STATUS_LABEL_KEY[attempt.status] ||
                        'MAIL_ORDER.PAYMENT_ATTEMPT.STATUS.PROCESSING',
                    })}
                  </span>
                </td>
                <td>{formatAmount(attempt.amountTND, 'TND')}</td>
                <td>{attempt.transactionId || '-'}</td>
                <td>{formatDateTime(attempt.createdAt)}</td>
                <td>{attempt.failureReason || '-'}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={6}>
                <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                  {intl.formatMessage({id: 'COMMON.EMPTY_DATA'})}
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )

  return (
    <>
      <AzPageHeader
        title={mailOrder ? mailOrder.circuitName : intl.formatMessage({id: 'MAIL_ORDER_PAGE.TITLE'})}
        subtitle={intl.formatMessage({id: 'MAIL_ORDER_PAGE.BREADCRUMB'})}
      />

      {!loading && mailOrder && (
        <div className='card shadow-sm border border-gray-200 mb-5'>
          <div className='card-body p-5 p-lg-6'>
            <div className='d-flex align-items-center gap-4'>
              <span className='symbol symbol-50px symbol-circle bg-light-primary'>
                <span className='symbol-label'>
                  <i className='bi bi-receipt fs-2 text-primary' />
                </span>
              </span>
              <div>
                <div className='d-flex align-items-center gap-3 mb-1 flex-wrap'>
                  <h3 className='mb-0 fs-3 fw-bolder text-gray-900'>{mailOrder.circuitName}</h3>
                  {renderStatusBadge(mailOrder.status)}
                </div>
                <div className='text-muted fs-7'>
                  <i className='bi bi-person me-1' />
                  {mailOrder.clientName}
                  <span className='mx-2'>·</span>
                  <i className='bi bi-envelope me-1' />
                  {mailOrder.clientEmail}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <KTCard>
        <div className='card-header border-0 pt-6 pb-5 align-items-center flex-wrap gap-4'>
          <div
            className='d-inline-flex flex-wrap align-items-center gap-1 p-1 bg-light rounded'
            style={{borderRadius: '0.65rem'}}
            role='tablist'
          >
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id
              const tabIcon =
                tab.id === 'overview'
                  ? 'bi-grid-1x2'
                  : tab.id === 'installments'
                  ? 'bi-calendar2-check'
                  : 'bi-clock-history'
              return (
                <button
                  key={tab.id}
                  type='button'
                  role='tab'
                  aria-selected={isActive}
                  className={`btn btn-sm fw-semibold px-4 py-2 ${
                    isActive ? 'btn-white text-primary shadow-sm' : 'btn-light text-gray-600'
                  }`}
                  style={{borderRadius: '0.5rem', border: 'none'}}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <i className={`bi ${tabIcon} me-2 ${isActive ? 'text-primary' : 'text-gray-500'}`} />
                  {tab.label}
                </button>
              )
            })}
          </div>

          <div className='card-toolbar ms-auto d-flex flex-wrap gap-2'>
            {!isPaid && !isCancelled && (
              <>
                <button
                  type='button'
                  className='btn btn-primary fw-semibold px-4 py-2'
                  style={{minWidth: '150px', borderRadius: '0.5rem'}}
                  onClick={() => navigate(`/apps/mail-orders/${id}/edit`)}
                >
                  <i className='bi bi-pencil-square me-2' />
                  {intl.formatMessage({id: 'MAIL_ORDER.EDIT'})}
                </button>
                <button
                  type='button'
                  className='btn btn-danger fw-semibold px-4 py-2'
                  style={{minWidth: '150px', borderRadius: '0.5rem'}}
                  onClick={handleCancel}
                >
                  <i className='bi bi-x-circle me-2' />
                  {intl.formatMessage({id: 'MAIL_ORDER.CANCEL'})}
                </button>
              </>
            )}
            <button
              type='button'
              className='btn btn-secondary fw-semibold px-4 py-2'
              style={{minWidth: '150px', borderRadius: '0.5rem'}}
              onClick={() => navigate('/apps/mail-orders')}
            >
              <KTSVG path='/media/icons/duotune/arrows/arr063.svg' className='svg-icon-3 me-1' />
              {intl.formatMessage({id: 'MAIL_ORDER.BACK_TO_LIST'})}
            </button>
          </div>
        </div>
        <KTCardBody className='pt-2 pb-6'>
          {!loading && mailOrder && (
            <>
              {activeTab === 'overview' && renderOverview()}
              {activeTab === 'installments' && renderInstallments()}
              {activeTab === 'history' && renderHistory()}
            </>
          )}
          {loading && <DataLoading />}
        </KTCardBody>
      </KTCard>
    </>
  )
}

export default MailOrderDetailsPage
